import { Loader } from '@mantine/core';
import { holdsPlace, minutesToHm, type LiveShiftView } from '@reditelji/shared';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCheckinMutations } from '../api/hooks';
import { useSession } from '../api/session';
import type { Shift, TimelinePayload } from '../api/types';
import type { LiveChip } from '../live/derive';
import { useTimeline } from '../api/hooks';
import { useNow } from '../live/clock';
import { checkinActions, checkinsByShift, liveChip, liveViewsByShift } from '../live/derive';
import { dayPhase, ini } from '../lib/ui';
import { makeAxis } from '../timeline/geometry';
import { useUi } from '../state/ui';
import {
  BUCKET,
  bucketSeverity,
  deriveHoEvents,
  KIND_STYLE,
  lapStyle,
  lapText,
  SEV_STYLE,
  statusOfFactory,
  type HoEvent,
} from './derive';
import './predaje.css';

type KindFilter = Record<'predaja' | 'nadaljevanje' | 'prevzem' | 'zaključek', boolean>;
type RiskFilter = { risk: boolean; unconf: boolean; crowd: boolean; open: boolean };

/** Status labels that owe nothing further before a handover (SPEC §1.1J). */
const SETTLED_LABELS = new Set(['potrjen', 'opravljeno', 'zunanja ekipa']);

export function PredajePage() {
  const { selectedDayId } = useUi();
  const timeline = useTimeline(selectedDayId);
  const clock = useNow();

  if (!selectedDayId || timeline.isPending) {
    return (
      <div className="ho-page" style={{ display: 'grid', placeItems: 'center' }}>
        <Loader />
      </div>
    );
  }
  if (!timeline.data) return null;
  return <HandoverBoard key={timeline.data.day.id} data={timeline.data} clock={clock} />;
}

function HandoverBoard({
  data,
  clock,
}: {
  data: TimelinePayload;
  clock: { isoDate: string; minutes: number };
}) {
  const navigate = useNavigate();
  const { selectShift, setSelectedDay } = useUi();
  const checkinMut = useCheckinMutations();
  // Recording a check-in is race-day work (SPEC §1.1L); the organisation watches.
  const { can } = useSession();
  const canAct = can('live:act');
  const phase = dayPhase(data.day.date, clock.isoDate);

  const [live, setLive] = useState(phase === 'live');
  // In live mode the cursor follows the clock until somebody moves it.
  const [pinned, setPinned] = useState<number | null>(phase === 'live' ? null : 860);
  // Past midnight the day just over is still being watched: it stands at its end.
  const now = pinned ?? (phase === 'live' ? clock.minutes : 1440);
  const setNow = (m: number) => setPinned(m);
  const [filters, setFilters] = useState<RiskFilter>({ risk: false, unconf: false, crowd: false, open: false });
  const [kinds, setKinds] = useState<KindFilter>({ predaja: true, nadaljevanje: false, prevzem: false, 'zaključek': false });
  const [selId, setSelId] = useState<string | null>(null);
  const cm = useMemo(() => checkinsByShift(data.checkins), [data.checkins]);

  const volunteersById = useMemo(() => new Map(data.volunteers.map((v) => [v.id, v])), [data.volunteers]);
  // What has happened by now: all of a day that is over, none of one ahead.
  const happenedBy = phase === 'live' ? now : phase === 'past' ? 1440 : -1;
  const evs = useMemo(() => deriveHoEvents(data, happenedBy), [data, happenedBy]);
  // The board's hours for this day, so the strip below and the timeline agree.
  const axis = useMemo(
    () => makeAxis(data.posts.flatMap((p) => [...p.windows, ...p.shifts])),
    [data.posts],
  );
  // Pull the cursor back onto the axis when the day changes. Clamping only what
  // the slider renders would leave the thumb at the edge while the readout, the
  // bucket highlight and the nudge guard all worked from a minute off the axis.
  useEffect(() => {
    setPinned((m) => (m === null ? null : Math.min(axis.toMin, Math.max(axis.fromMin, m))));
  }, [axis]);
  const preds = evs.filter((e) => e.kind === 'predaja');
  const cont = evs.filter((e) => e.kind === 'nadaljevanje');
  const bsev = useMemo(() => bucketSeverity(evs), [evs]);

  // A proposal nobody accepted names nobody: the place is empty.
  const name = (s: Shift | null) => (s && holdsPlace(s) ? (volunteersById.get(s.volunteerId!)?.name ?? '?') : null);
  const staffed = (s: Shift | null) => s !== null && holdsPlace(s);
  const groupById = new Map(data.externalGroups.map((g) => [g.id, g]));
  /** The team behind this shift, when it is a team's (SPEC §1.1J). */
  const teamOf = (s: Shift | null) => {
    const gid = s?.volunteerId ? volunteersById.get(s.volunteerId)?.externalGroupId : null;
    return gid ? (groupById.get(gid) ?? null) : null;
  };
  /**
   * Still owes us a confirmation before the handover.
   *
   * The filter, its chip count and the card colour all asked this separately as
   * `!== 'potrjen' && !== 'opravljeno'`, which makes any new status default to
   * "unconfirmed" — so a team would be counted forever on a filter that can
   * never reach zero. `settled()` in ./derive holds the same line for the
   * warnings; this is its counterpart on the page.
   */
  const awaitsConfirm = (list: Shift[]) =>
    list.some((sh) => staffed(sh) && !SETTLED_LABELS.has(statusLabel(sh)));

  const statusOf = useMemo(() => statusOfFactory(data.volunteers), [data.volunteers]);
  const statusLabel = (s: Shift) => {
    if (s.state === 'done') return 'opravljeno';
    if (!staffed(s)) return 'nezasedeno';
    if (teamOf(s)) return 'zunanja ekipa';
    return statusOf(s);
  };
  // Back-to-back shifts read as one stretch, as on the map (`liveShiftViews`).
  const views = useMemo(() => liveViewsByShift(data.posts, cm, now), [data.posts, cm, now]);
  /** Where this person stands right now, on race day; null before it. */
  const chipOf = (s: Shift) => (live ? liveChip(s, cm, now, teamOf(s) !== null, views.get(s.id)) : null);

  const shown = evs
    .filter((e) => {
      if (!kinds[e.kind]) return false;
      if (filters.risk && e.sev === 'ok') return false;
      if (filters.unconf && !awaitsConfirm(e.ins)) return false;
      if (filters.crowd && e.crowd < 3) return false;
      if (filters.open && e.done) return false;
      if (live && (e.at < now - 60 || e.at > now + 180)) return false;
      return true;
    })
    .sort((a, b) => a.at - b.at || (a.post.code < b.post.code ? -1 : 1));

  const hcount = new Map<number, number>();
  for (const e of preds) {
    const h = Math.floor(e.at / 60);
    hcount.set(h, (hcount.get(h) ?? 0) + 1);
  }
  const hoursMap = new Map<number, HoEvent[]>();
  for (const e of shown) {
    const h = Math.floor(e.at / 60);
    const list = hoursMap.get(h) ?? [];
    list.push(e);
    hoursMap.set(h, list);
  }
  const hours = [...hoursMap.keys()].sort((a, b) => a - b);

  const bcount = new Map<number, number>();
  for (const e of preds) {
    const b = Math.floor(e.at / BUCKET) * BUCKET;
    bcount.set(b, (bcount.get(b) ?? 0) + 1);
  }
  const maxB = Math.max(1, ...bcount.values());

  const sel = selId ? (evs.find((e) => e.id === selId) ?? null) : null;

  const stats = [
    { value: preds.length, label: 'predaj', fg: 'oklch(0.3 0.012 90)' },
    { value: preds.filter((e) => e.sev === 'crit').length, label: 'vrzeli / brez zamenjave', fg: 'oklch(0.5 0.13 25)' },
    { value: preds.filter((e) => e.lap === 0 && e.post.urgent && !e.covered).length, label: 'nujna brez prekrivanja', fg: 'oklch(0.5 0.1 75)' },
    { value: preds.filter((e) => e.done).length, label: 'opravljenih', fg: 'oklch(0.42 0.08 155)' },
  ];

  const filterDefs: { key: keyof RiskFilter; label: string; dot: string; count: number }[] = [
    { key: 'risk', label: 'tvegane predaje', dot: 'oklch(0.58 0.13 25)', count: preds.filter((e) => e.sev !== 'ok').length },
    {
      key: 'unconf',
      label: 'zamenjava ni potrjena',
      dot: 'oklch(0.68 0.11 75)',
      count: preds.filter((e) => awaitsConfirm(e.ins)).length,
    },
    { key: 'crowd', label: 'gneča (3+ hkrati)', dot: 'oklch(0.62 0.09 285)', count: preds.filter((e) => e.crowd >= 3).length },
    { key: 'open', label: 'zamenjava še ni prijavljena', dot: 'oklch(0.62 0.09 245)', count: preds.filter((e) => !e.done).length },
  ];
  const kindDefs: { key: keyof KindFilter; label: string }[] = [
    { key: 'predaja', label: 'Predaje' },
    { key: 'nadaljevanje', label: `Nadaljevanja (${cont.length})` },
    { key: 'prevzem', label: 'Odprtja' },
    { key: 'zaključek', label: 'Zaprtja' },
  ];

  function record(s: Shift, kind: 'in' | 'out') {
    checkinMut.set.mutate({ shiftId: s.id, kind });
  }

  function openInTimeline(e: HoEvent) {
    const target = e.ins[0] ?? e.outs[0];
    if (!target) return;
    setSelectedDay(data.day.id);
    selectShift(target.id);
    void navigate('/casovnica');
  }

  function openPerson(s: Shift) {
    const team = teamOf(s);
    if (team) {
      void navigate(`/prostovoljci?view=ekipe&ekipa=${team.id}`);
    } else if (s.volunteerId) {
      void navigate(`/prostovoljci?volunteer=${s.volunteerId}`);
    } else {
      setSelectedDay(data.day.id);
      selectShift(s.id);
      void navigate('/casovnica');
    }
  }

  const mkCountdown = (e: HoEvent) => {
    if (!live) return null;
    const cd = e.at - now;
    if (Math.abs(cd) > 180) return null;
    const text = cd > 0 ? `čez ${cd} min` : cd === 0 ? 'zdaj' : `pred ${-cd} min`;
    return { text, red: cd <= 15 && cd >= -15 };
  };

  return (
    <div className="ho-page">
      <div className="ho-main">
        <div className="ho-head">
          <div className="ho-head-row">
            <button
              type="button"
              className={`ho-live${live ? ' ho-live--on' : ''}`}
              onClick={() => {
                setLive((v) => !v);
                // Only today has a clock to follow; any other day keeps its minute.
                setPinned(!live && phase === 'live' ? null : now);
              }}
            >
              <span className="ho-live-dot" />
              {live ? 'V živo' : 'Načrtovanje'}
            </button>
            <input
              type="range"
              min={axis.fromMin}
              max={axis.toMin}
              step={5}
              value={now}
              onChange={(e) => setNow(Number(e.currentTarget.value))}
              className="ho-slider"
            />
            <span className="ho-now">{minutesToHm(now)}</span>
            {live && phase === 'live' && pinned !== null ? (
              <button type="button" className="ho-live" onClick={() => setPinned(null)}>
                Na zdaj
              </button>
            ) : null}
            <span className="ho-rule">Pravilo: odhajajoči ostane, dokler zamenjava ne pride.</span>
            <div className="ho-stats">
              {stats.map((s) => (
                <span key={s.label} className="ho-stat">
                  <span className="ho-stat-val" style={{ color: s.fg }}>
                    {s.value}
                  </span>
                  <span className="ho-stat-label">{s.label}</span>
                </span>
              ))}
            </div>
          </div>

          {bcount.size > 0 ? (
            <div className="ho-buckets">
              {Array.from({ length: axis.spanMin / BUCKET }, (_, i) => {
                const b = axis.fromMin + i * BUCKET;
                const c = bcount.get(b) ?? 0;
                const sv = bsev.get(b);
                const isNow = live && now >= b && now < b + BUCKET;
                return (
                  <div key={b} className="ho-bucket" onClick={() => { setLive(true); setNow(b); }}>
                    <div
                      className="ho-bucket-bar"
                      title={`${minutesToHm(b)}–${minutesToHm(b + BUCKET)}: ${c} predaj`}
                      style={{
                        height: c ? Math.max(4, Math.round((c / maxB) * 32)) : 2,
                        background: !c
                          ? 'oklch(0.955 0.005 95)'
                          : sv === 'crit'
                            ? 'oklch(0.7 0.13 25)'
                            : sv === 'warn'
                              ? 'oklch(0.78 0.1 75)'
                              : 'oklch(0.74 0.08 155)',
                        borderTop: isNow ? '2px solid oklch(0.45 0.14 25)' : '0',
                      }}
                    />
                    <span className="ho-bucket-label" style={isNow ? { color: 'oklch(0.45 0.14 25)' } : undefined}>
                      {b % 120 === 0 ? b / 60 : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}

          <div className="ho-filters">
            {filterDefs.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`ho-chip${filters[f.key] ? ' ho-chip--on' : ''}`}
                onClick={() => setFilters((prev) => ({ ...prev, [f.key]: !prev[f.key] }))}
              >
                <span className="ho-chip-dot" style={{ background: f.dot }} />
                {f.label}
                <span className="ho-chip-count">{f.count}</span>
              </button>
            ))}
            <span className="ho-filter-sep" />
            {kindDefs.map((k) => (
              <button
                key={k.key}
                type="button"
                className={`ho-chip${kinds[k.key] ? ' ho-chip--on' : ''}`}
                onClick={() => setKinds((prev) => ({ ...prev, [k.key]: !prev[k.key] }))}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>

        <div className="ho-scroll">
          {shown.length === 0 ? (
            <div className="ho-empty">Ni predaj, ki bi ustrezale filtrom.</div>
          ) : (
            hours.map((h) => {
              const list = hoursMap.get(h)!;
              const crit = list.filter((x) => x.sev === 'crit').length;
              const pred = list.filter((x) => x.kind === 'predaja').length;
              const all = hcount.get(h) ?? 0;
              const isNow = live && Math.floor(now / 60) === h;
              return (
                <div key={h} className="ho-hour">
                  <div className="ho-hour-head">
                    <span className="ho-hour-label">{h}:00</span>
                    {isNow ? <span className="ho-zdaj">ZDAJ</span> : null}
                    <span className="ho-hour-meta">
                      {pred} predaj
                      {pred !== all ? ` od ${all}` : ''}
                      {crit ? ` · ${crit} tveganih` : ''}
                    </span>
                    {all >= 6 ? <span className="ho-hour-crowd">gneča — {all} predaj v tej uri</span> : null}
                  </div>
                  <div className="ho-cards">
                    {list.map((e) => (
                      <EventCard
                        key={e.id}
                        e={e}
                        selected={selId === e.id}
                        countdown={mkCountdown(e)}
                        live={live}
                        nameOf={name}
                        chipOf={chipOf}
                        statusLabel={statusLabel}
                        onSelect={() => setSelId(e.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <aside className="ho-aside">
        {sel ? (
          <SelectedPanel
            e={sel}
            nameOf={name}
            volunteersById={volunteersById}
            statusLabel={statusLabel}
            chipOf={chipOf}
            views={views}
            now={now}
            teamOf={teamOf}
            // Only today, and only at the minute it is now: the server stamps a
            // check-in with its own clock.
            onRecord={canAct && live && phase === 'live' && pinned === null ? record : undefined}
            busy={checkinMut.set.isPending}
            onOpenTimeline={() => openInTimeline(sel)}
            onOpenPerson={openPerson}
          />
        ) : (
          <div className="ho-aside-empty">Izberi predajo, da vidiš, kdo odhaja in kdo prihaja, s kontakti.</div>
        )}
      </aside>
    </div>
  );
}

/** One person's name on a card, with where they stand right now. */
function PersonLine({
  s,
  pname,
  chip,
  settledStatus,
  strong,
}: {
  s: Shift;
  pname: string | null;
  chip: LiveChip | null;
  settledStatus: boolean;
  strong: boolean;
}) {
  return (
    <span className="ho-card-person">
      <span
        className={strong ? 'ho-card-in' : 'ho-card-out'}
        style={{
          color: !pname
            ? 'oklch(0.5 0.13 25)'
            : strong && !chip && !settledStatus
              ? 'oklch(0.45 0.09 75)'
              : undefined,
        }}
      >
        {pname ?? (strong ? 'ZAMENJAVA MANJKA' : 'PROSTO')}
      </span>
      {chip && pname ? <span className={`ho-live-chip ho-live-chip--${chip.status}`}>{chip.text}</span> : null}
      {s.state === 'done' && !chip ? <span className="ho-live-chip ho-live-chip--odjavljen">opravljeno</span> : null}
    </span>
  );
}

function progressText(e: HoEvent): { text: string; tone: 'done' | 'part' | 'wait' | 'bad' } | null {
  if (e.kind === 'nadaljevanje') return null;
  const { done, expected, complete } = e.progress;
  if (complete) return { text: e.kind === 'zaključek' ? '✓ vsi odjavljeni' : '✓ opravljena', tone: 'done' };
  if (e.kind !== 'zaključek' && e.ins.some((s) => !holdsPlace(s))) return { text: 'zamenjave ni', tone: 'bad' };
  if (done > 0) return { text: `${e.kind === 'zaključek' ? 'odjavljena' : 'prišla'} ${done} od ${expected}`, tone: 'part' };
  return { text: e.kind === 'zaključek' ? 'čaka odjavo' : 'čaka prihod', tone: 'wait' };
}

function EventCard({
  e,
  selected,
  countdown,
  live,
  nameOf,
  chipOf,
  statusLabel,
  onSelect,
}: {
  e: HoEvent;
  selected: boolean;
  countdown: { text: string; red: boolean } | null;
  live: boolean;
  nameOf: (s: Shift | null) => string | null;
  chipOf: (s: Shift) => LiveChip | null;
  statusLabel: (s: Shift) => string;
  onSelect: () => void;
}) {
  const s = SEV_STYLE[e.sev];
  const k = KIND_STYLE[e.kind];
  const ls = lapStyle(e);
  const prog = live ? progressText(e) : null;
  return (
    <div
      className="ho-card"
      style={{
        background: e.done ? 'oklch(0.985 0.012 155)' : s.bg,
        border: `1px ${e.done ? 'solid' : s.bs} ${e.done ? 'oklch(0.88 0.04 155)' : selected ? 'oklch(0.6 0.1 245)' : s.bd}`,
        boxShadow: selected ? '0 0 0 1px oklch(0.6 0.1 245)' : 'none',
      }}
      onClick={onSelect}
    >
      <div className="ho-card-left">
        <span className="ho-card-time" style={{ color: e.done ? 'oklch(0.55 0.06 155)' : 'oklch(0.24 0.012 90)' }}>
          {minutesToHm(e.at)}
        </span>
        <span className="ho-card-kind" style={{ color: k.fg }}>
          {k.label}
        </span>
        {countdown ? (
          <span
            className="ho-card-cd"
            style={{ color: countdown.red ? 'oklch(0.5 0.13 25)' : 'oklch(0.6 0.012 90)' }}
          >
            {countdown.text}
          </span>
        ) : null}
      </div>
      <div className="ho-card-mid">
        <div className="ho-card-post">
          <span className="ho-card-code">{e.post.code}</span>
          {e.post.urgent ? <span className="ho-card-urgent">NUJEN</span> : null}
          <span className="ho-card-micro">{e.post.micro}</span>
        </div>
        <div className="ho-card-pair">
          <div className="ho-card-side">
            {e.outs.length === 0 ? <span className="ho-card-out">—</span> : null}
            {e.outs.map((sh) => (
              <PersonLine
                key={sh.id}
                s={sh}
                pname={nameOf(sh)}
                chip={chipOf(sh)}
                settledStatus={SETTLED_LABELS.has(statusLabel(sh))}
                strong={false}
              />
            ))}
          </div>
          <span className="ho-card-arrow">→</span>
          <div className="ho-card-side">
            {e.ins.length === 0 ? <span className="ho-card-out">konec</span> : null}
            {e.ins.map((sh) => (
              <PersonLine
                key={sh.id}
                s={sh}
                pname={nameOf(sh)}
                chip={chipOf(sh)}
                settledStatus={SETTLED_LABELS.has(statusLabel(sh))}
                strong
              />
            ))}
          </div>
        </div>
        <div className="ho-card-pills">
          {prog ? <span className={`ho-pill ho-pill--${prog.tone}`}>{prog.text}</span> : null}
          <span className="ho-pill" style={{ background: ls.bg, color: ls.fg }}>
            {lapText(e)}
          </span>
          {e.crowd >= 3 ? <span className="ho-pill ho-pill--crowd">{e.crowd} predaj v isti pol uri</span> : null}
        </div>
      </div>
    </div>
  );
}

function SelectedPanel({
  e,
  nameOf,
  volunteersById,
  statusLabel,
  chipOf,
  views,
  now,
  teamOf,
  onRecord,
  busy,
  onOpenTimeline,
  onOpenPerson,
}: {
  e: HoEvent;
  nameOf: (s: Shift | null) => string | null;
  volunteersById: Map<string, { name: string; phone: string; externalPersonName: string }>;
  statusLabel: (s: Shift) => string;
  chipOf: (s: Shift) => LiveChip | null;
  views: Map<string, LiveShiftView>;
  now: number;
  teamOf: (s: Shift | null) => { id: string; name: string; leaderName: string; leaderPhone: string } | null;
  /** Absent for a login that may only watch, and outside live mode. */
  onRecord?: (s: Shift, kind: 'in' | 'out') => void;
  busy: boolean;
  onOpenTimeline: () => void;
  onOpenPerson: (s: Shift) => void;
}) {
  const ls = lapStyle(e);
  const headline =
    e.kind === 'predaja' && e.covered
      ? 'Drugi redar ostane na mestu — mesto ni nikoli prazno'
      : e.kind === 'predaja'
      ? e.lap !== null && e.lap > 0
        ? `Prekrivanje ${e.lap} min — zamenjava pride pred odhodom`
        : e.lap === 0
          ? 'Predaja iz minute v minuto — brez prekrivanja'
          : `Vrzel ${-(e.lap ?? 0)} min — mesto ostane prazno`
      : e.kind === 'nadaljevanje'
        ? 'Isti redar nadaljuje — predaja ni potrebna'
        : e.kind === 'prevzem'
          ? 'Odprtje mesta — prva izmena'
          : 'Zaprtje mesta — zadnja izmena';

  const people: { role: string; shift: Shift }[] = [
    ...e.outs.map((shift) => ({ role: 'ODHAJA', shift })),
    ...e.ins.map((shift) => ({ role: e.kind === 'prevzem' ? 'PREVZAME' : 'PRIHAJA', shift })),
  ];
  // Somebody is leaving, somebody has come: they may go, whoever is still on the way.
  const partly = e.kind === 'predaja' && !e.done && e.progress.done > 0 && e.outs.some((sh) => holdsPlace(sh) && !teamOf(sh));

  return (
    <div className="ho-sel">
      <div className="ho-sel-head">
        <span className="ho-sel-time">{minutesToHm(e.at)}</span>
        <span className="ho-sel-code">{e.post.code}</span>
        <span className="ho-sel-micro">{e.post.micro}</span>
      </div>

      <div
        className="ho-sel-lap"
        style={{
          background: ls.bg === 'oklch(0.968 0.005 95)' ? 'oklch(0.978 0.004 95)' : ls.bg,
          borderColor:
            e.sev === 'crit' ? 'oklch(0.88 0.05 25)' : e.sev === 'warn' ? 'oklch(0.89 0.055 75)' : 'oklch(0.9 0.03 155)',
          color: ls.fg,
        }}
      >
        <span className="ho-sel-lap-dot" style={{ background: SEV_STYLE[e.sev].dot }} />
        {headline}
      </div>

      {people.map(({ role, shift: sh }) => {
        const pname = nameOf(sh);
        const vol = sh.volunteerId ? volunteersById.get(sh.volunteerId) : undefined;
        const team = teamOf(sh);
        const st = statusLabel(sh);
        const chip = chipOf(sh);
        const view = views.get(sh.id);
        const may = view ? checkinActions(sh, view, now, team !== null) : { in: false, out: false };
        return (
          <div key={sh.id} className="ho-person">
            <span
              className="ho-person-avatar"
              style={
                pname
                  ? { background: 'oklch(0.93 0.02 245)', color: 'oklch(0.4 0.06 245)' }
                  : { background: 'oklch(0.95 0.03 25)', color: 'oklch(0.45 0.11 25)' }
              }
            >
              {pname ? ini(pname) : '?'}
            </span>
            <div className="ho-person-main">
              <div className="ho-person-role">{role}</div>
              <div className="ho-person-name" style={{ color: pname ? 'oklch(0.26 0.012 90)' : 'oklch(0.5 0.12 25)' }}>
                {pname ?? 'Nezasedeno'}
              </div>
              <div className="ho-person-sub">
                {minutesToHm(sh.fromMin)}–{minutesToHm(sh.toMin)} ·{' '}
                {(() => {
                  // Name and number have to come from the same person. The place
                  // itself when the team told us who is coming, otherwise the
                  // leader — never one's name against the other's number.
                  const who = team
                    ? vol?.phone
                      ? { name: vol.externalPersonName, phone: vol.phone }
                      : { name: team.leaderName || team.name, phone: team.leaderPhone }
                    : { name: '', phone: vol?.phone ?? '' };
                  if (!who.phone) return team ? `${team.name} · brez telefona` : '—';
                  return (
                    <>
                      {who.name ? `${who.name} ` : ''}
                      <a className="tl-live-tel" href={`tel:${who.phone}`}>
                        {who.phone}
                      </a>
                    </>
                  );
                })()}
              </div>
              <div className="ho-person-chips">
                {chip ? <span className={`ho-live-chip ho-live-chip--${chip.status}`}>{chip.text}</span> : null}
                {pname && !team ? (
                  <span
                    className="ho-person-status"
                    style={
                      st === 'potrjen' || st === 'opravljeno'
                        ? { background: 'oklch(0.95 0.035 155)', color: 'oklch(0.34 0.06 155)' }
                        : st === 'zavrnjen'
                          ? { background: 'oklch(0.95 0.04 25)', color: 'oklch(0.44 0.12 25)' }
                          : { background: 'oklch(0.965 0.035 75)', color: 'oklch(0.44 0.09 75)' }
                    }
                  >
                    {st}
                  </span>
                ) : null}
              </div>
              {onRecord && (may.in || may.out) ? (
                <div className="ho-person-actions">
                  {may.in ? (
                    <button type="button" className="ho-action ho-action--pri" disabled={busy} onClick={() => onRecord(sh, 'in')}>
                      Prijavi prihod
                    </button>
                  ) : null}
                  {may.out ? (
                    <button type="button" className="ho-action" disabled={busy} onClick={() => onRecord(sh, 'out')}>
                      Odjavi
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <button type="button" className="ho-person-open" onClick={() => onOpenPerson(sh)}>
              Odpri
            </button>
          </div>
        );
      })}

      {partly ? <div className="ho-ok">Mesto je pokrito — odhajajoči lahko odide.</div> : null}

      {e.warns.length > 0 ? (
        <>
          <div className="ho-sel-eyebrow">Tveganja te predaje</div>
          {e.warns.map((w) => (
            <div key={w} className="ho-warn">
              {w}
            </div>
          ))}
        </>
      ) : null}

      <div className="ho-sel-eyebrow">UKREPI</div>
      <div className="ho-actions">
        <button type="button" className="ho-action" onClick={onOpenTimeline}>
          Odpri na časovnici
        </button>
        {people[0] ? (
          <button type="button" className="ho-action" onClick={() => onOpenPerson((e.ins[0] ?? e.outs[0])!)}>
            Odpri osebo
          </button>
        ) : null}
      </div>
    </div>
  );
}
